import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { RECREATE_SCHEDULER } from '../../../services/schedule';
import { publicClient } from '~/services';
import { motion } from 'motion/react';

const CreateNewSchedule = () => {
  const { t } = useTranslation('schedule');
  const { schedule_id } = useParams<{ schedule_id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasExecuted = useRef(false);

  const [recreateScheduler] = useMutation(RECREATE_SCHEDULER, {
    client: publicClient,
  });

  const handleRecreateSchedule = useCallback(async () => {
    if (!schedule_id || hasExecuted.current) return;
    
    hasExecuted.current = true;
    setLoading(true);
    setError(null);

    try {
      const { data } = await recreateScheduler({
        variables: {
          data: {
            scheduleId: schedule_id,
          },
        },
      });

      if (data.recreate_scheduler.hasError) {
        setError(data.recreate_scheduler.message);
        setLoading(false);
        return;
      }

      const newScheduleId = data.recreate_scheduler.data?.schedule?._id;
      
      if (newScheduleId) {
        // Redirigir al calendario con el nuevo schedule ID
        navigate(`/calendar/${newScheduleId}/scheduler`);
      } else {
        setError(t('link_generation_error', 'There was an error creating the link for this schedule. Please try again.'));
        setLoading(false);
      }
    } catch (error: any) {
      console.error('Error recreating schedule:', error);
      setError(error.message || t('create_error', 'Error creating the schedule. Please try again.'));
      setLoading(false);
    }
  }, [schedule_id, recreateScheduler, navigate, t]);

  useEffect(() => {
    if (schedule_id && !hasExecuted.current) {
      handleRecreateSchedule();
    }
  }, [schedule_id, handleRecreateSchedule]);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="max-w-md w-full mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="alert alert-error"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </motion.div>
          <div className="flex justify-center mt-4">
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/schedule/home')}
            >
              {t('back_to_schedules', 'Back to Schedules')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-base-200">
      <div className="max-w-md w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card bg-base-100 shadow-xl"
        >
          <div className="card-body items-center text-center">
            {/* Loading Animation */}
            <motion.div
              className="relative"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              {/* Main spinner */}
              <motion.div
                className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
              
              {/* Inner accent circle */}
              <motion.div
                className="absolute top-1/2 left-1/2 w-8 h-8 bg-primary rounded-full transform -translate-x-1/2 -translate-y-1/2"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.7, 1, 0.7],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </motion.div>

            {/* Animated text */}
            <motion.div
              className="mt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="card-title text-2xl mb-2">
                {t('generating_link', 'Generating link...')}
              </h2>
              
              <motion.p
                className="text-base-content/70"
                animate={{
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                {t('creating_schedule', 'Creating your new schedule...')}
              </motion.p>
            </motion.div>

            {/* Dots animation */}
            <div className="flex space-x-1 mt-4">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="w-2 h-2 bg-primary rounded-full"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: i * 0.2,
                    ease: "easeInOut",
                  }}
                />
              ))}
            </div>

            {/* Schedule ID display */}
            {schedule_id && (
              <motion.div
                className="mt-4 text-xs text-base-content/50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                ID: {schedule_id}
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Background decoration */}
        <motion.div
          className="absolute inset-0 -z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.1 }}
        >
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-4 h-4 bg-primary rounded-full"
              style={{
                left: `${20 + (i * 15)}%`,
                top: `${20 + (i * 10)}%`,
              }}
              animate={{
                y: [0, -10, 0],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: i * 0.5,
                ease: "easeInOut",
              }}
            />
          ))}
        </motion.div>
      </div>
    </div>
  );
}

export default CreateNewSchedule;
